import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';

import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';

import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';

import { useRouteMatchParam } from '@/hooks';

import { PageProps } from '@/types/PageProps';

import WorkOrderComplementTabs from '@/components/Movements/WorkOrders/Details/Complements/WorkOrderComplementTabs';
import ComplementHeader from '@/components/Movements/WorkOrders/Details/Complements/ComplementHeader';
import {
  useAccountReceivableFinancialPending,
  useAccountReceivableFinancialSummary,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { Divider, Empty, Skeleton, Tabs } from 'antd';
import WorkOrderFooter from '@/components/Movements/WorkOrders/Details/WorkOrderFooter';
import WorkOrderFinancialConfig from '@/components/Movements/WorkOrders/Details/WorkOrderFinancial/WorkOrderFinancialConfig';
import WorkOrderFinancialSummary from '@/components/Movements/WorkOrders/Details/WorkOrderFinancial/WorkOrderFinancialSummary';
import { useWorkorderForms } from '@/components/Movements/WorkOrders/Details/WorkOrderFormProvider';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';

type TabItem = {
  key: string;
  label: string;
  children: React.ReactNode;
};

function WorkOrderFinancialPage({ route }: PageProps) {
  const intl = useIntl();
  const workOrderId = useRouteMatchParam('workOrderId');
  const { data: workOrder, isFetching } = useWorkOrder(+workOrderId);
  const { hasFormChange } = useWorkorderForms();

  const {
    complement: { complementIdentifier },
  } = useWorkorderDetailContext();

  const workOrderDisplayNumber = `${workOrder?.number
    .toString()
    .padStart(6, '0')}`;

  useUpdateBreadcrumbParam(':workOrderId', workOrderDisplayNumber);

  const financialPending = useAccountReceivableFinancialPending(workOrder?.id);

  const financialSummary = useAccountReceivableFinancialSummary(workOrder?.id);
  const showInsurer =
    !!financialSummary.data?.installmentReceivableInsurer?.length;
  const showPrivate =
    !!financialSummary.data?.installmentReceivableCustomer?.length;

  const isLoading = financialPending.isFetching || financialSummary.isFetching;

  const refetchFinancial = async () => {
    await financialPending.refetch();
    await financialSummary.refetch();
  };

  const tabContens: Record<string, TabItem & { visible: boolean }> = {
    totalPrivateValue: {
      label: 'Gerar financeiro particular',
      key: '1',
      visible: !!financialPending.data?.totalPrivateValue,
      children: (
        <WorkOrderFinancialConfig
          onChange={() => null}
          financialPendingData={financialPending?.data}
          type="private"
          key={financialPending.dataUpdatedAt}
          refetch={refetchFinancial}
        />
      ),
    },
    totalInsurerValue: {
      label: 'Gerar financeiro seguradora',
      key: '2',
      visible: !!financialPending.data?.totalInsurerValue,
      children: (
        <WorkOrderFinancialConfig
          onChange={() => null}
          financialPendingData={financialPending.data}
          type="insurer"
          key={financialPending.dataUpdatedAt}
          refetch={refetchFinancial}
        />
      ),
    },
    totalValuePendent: {
      label: 'Resumo financeiro',
      key: '3',
      visible: showPrivate || showInsurer,
      children: (
        <WorkOrderFinancialSummary
          financialSummary={financialSummary.data}
          loading={financialSummary.isFetching}
        />
      ),
    },
  };

  const items = Object.keys(tabContens)
    .map((key) => tabContens[key])
    .filter((item) => !!item.visible);
  const hasItems = !!items.length;

  return (
    <>
      <Helmet
        title={`${
          complementIdentifier ? `${complementIdentifier} | ` : ''
        }${intl.formatMessage({
          id: route.name,
        })}`}
      />

      <WorkOrderComplementTabs
        route={route.parent}
        isLoading={isFetching}
        permissions={route.permissions}
      >
        <ComplementHeader />

        <div id="complement-financial-wrapper">
          {isLoading && <Skeleton loading={isLoading} />}

          {!isLoading && hasItems && (
            <Tabs items={items} id="workorder-complement-financial" />
          )}

          {!hasItems && !isLoading && (
            <Empty
              description="Não há dados para gerar financeiro"
              style={{ margin: '100px 0' }}
            />
          )}
        </div>

        <Divider />

        <WorkOrderFooter disabled={!hasFormChange} />
      </WorkOrderComplementTabs>
    </>
  );
}

export default WorkOrderFinancialPage;
